import { Avatar } from "@logora/debate.user.avatar";
import { AssembleeHemicycle } from "@logora/parliament.parliament_chamber.assemble_hemicycle";
import { SenatHemicycle } from "@logora/parliament.parliament_chamber.senat_hemicycle";
import { GroupLabel } from "@logora/parliament.parliament_group.group_label";
import { MemberBox } from "@logora/parliament.parliament_member.member_box";
import { Box as UiBox } from "@logora/parliament.ui.box";
import CloseIcon from "@mui/icons-material/Close";
import EmailIcon from "@mui/icons-material/Email";
import HomeIcon from "@mui/icons-material/Home";
import LanguageIcon from "@mui/icons-material/Language";
import PhoneIcon from "@mui/icons-material/Phone";
import XIcon from "@mui/icons-material/X";
import { Box, Stack, Typography } from "@mui/material";
import { Drawer } from "@mui/material";
import cx from "classnames";
import React from "react";
import { useRedirect } from "react-admin";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { AlertButton } from "../alerts";
import { AmendementShow } from "../amendements";
import { Show } from "../components/show";
import {
	amendementDataset,
	documentDataset,
	dossierDataset,
	interventionDataset,
	questionDataset,
	voteDataset,
} from "../datasets";
import { GOUVERNEMENT_IMAGE_URL } from "../formatters";
import { formatDateNaisToAge } from "../formatters";
import { MultiList } from "../search/MultiList";
import { ActeurDashboard } from "./ActeurDashboard";
import styles from "./ActeurShow.module.scss";

export const ActeurDetails = ({ resource }) => {
	const redirectTo = useRedirect();
	const [searchParams] = useSearchParams();
	const adresses = [
		...resource.adressesElectroniques,
		...resource.adressesPostales,
	];

	const { dataset, amendementId } = useParams();
	const navigate = useNavigate();

	const displayContacts = () => {
		const phone = adresses.find(
			(adresse) => adresse.typeLibelle === "Téléphone",
		);
		const email = adresses.find((adresse) => adresse.typeLibelle === "Mèl");
		const publishedAdressePostale = adresses.find(
			(adresse) => adresse.typeLibelle === "Adresse publiée de circonscription",
		);
		const defaultAdressePostale = adresses.find(
			(adresse) => adresse.typeLibelle === "Adresse officielle",
		);
		const adresseToDisplay = publishedAdressePostale || defaultAdressePostale;

		const adressePostale = adresseToDisplay
			? `${adresseToDisplay.numeroRue || ""} ${
					adresseToDisplay.nomRue || ""
				} ${adresseToDisplay.codePostal || ""} ${adresseToDisplay.ville || ""}`
			: null;

		if (!phone && !email && !adressePostale) {
			return null;
		}

		return (
			<Box display={"flex"} flexDirection={"column"}>
				<span className={styles.contactLine}>
					<PhoneIcon sx={{ marginRight: "0.5em" }} />{" "}
					{phone ? phone.valElec : "Non disponible"}
				</span>
				<span className={styles.contactLine}>
					<HomeIcon sx={{ marginRight: "0.5em" }} /> {adressePostale}
				</span>
				<span className={styles.contactLine}>
					<EmailIcon sx={{ marginRight: "0.5em" }} />{" "}
					{email ? email.valElec : "Non disponible"}
				</span>
			</Box>
		);
	};

	const displayWebAddresses = () => {
		const website = adresses.find(
			(adresse) => adresse.typeLibelle === "Site internet",
		)
			? `https://${
					adresses.filter((a) => a.typeLibelle === "Site internet")[0]?.valElec
				}`
			: undefined;
		const twitter = resource.compteTwitter
			? `https://x.com/${resource.compteTwitter?.substring(1)}`
			: undefined;

		return (
			<>
				{twitter && (
					<a
						target="_blank"
						rel="noreferrer noopener"
						className={cx(styles.button, styles.buttonRounded)}
						href={twitter}
					>
						<XIcon />
					</a>
				)}
				{website && (
					<a
						target="_blank"
						rel="noreferrer noopener"
						className={cx(styles.button, styles.buttonRounded)}
						href={website}
					>
						<LanguageIcon />
					</a>
				)}
			</>
		);
	};

	const displayActifMandat = () => {
		const { mandatPrincipal, nombreMandats } = resource;
		const cardinal =
			nombreMandats > 1
				? `(${nombreMandats}ème mandat)`
				: nombreMandats === 1
					? `(${nombreMandats}er mandat)`
					: null;

		if (resource.actif && mandatPrincipal) {
			return (
				<Typography variant="body2" sx={{ color: "#777777" }}>
					Mandat en cours depuis le{" "}
					{new Date(mandatPrincipal.dateDebut)?.toLocaleDateString("fr-FR", {
						year: "numeric",
						month: "long",
						day: "numeric",
					}) ?? ""}{" "}
					{cardinal}
				</Typography>
			);
		}

		return (
			<Typography variant="body2" sx={{ color: "#777777" }}>
				Pas de mandat en cours
			</Typography>
		);
	};

	const isGouvernement = () =>
		resource.mandatPrincipal?.typeOrgane === "GOUVERNEMENT";

	return (
		<>
			<div className={styles.mainContainer}>
				<Box className={styles.avatarBackground}>
					<Stack direction="row" gap={2}>
						<Avatar
							avatarUrl={
								isGouvernement() ? GOUVERNEMENT_IMAGE_URL : resource.urlImage
							}
							size={124}
						/>
						<Box display={"column"}>
							<Typography sx={{ fontSize: "clamp(24px, 4vw, 34px)" }}>
								{resource.prenom} {resource.nom}
							</Typography>
							{resource.circonscription?.libelle.length > 0 ? (
								<Typography
									className={styles.link}
									variant="body2"
									sx={{
										color: "#777777",
										fontSize: "clamp(12px, 1.5vw, 16px)",
									}}
									onClick={() =>
										redirectTo(
											`/parliament/organes/${resource.circonscriptionUid}`,
										)
									}
								>
									{resource.circonscription?.libelle}
								</Typography>
							) : (
								<Typography
									className={styles.link}
									variant="body2"
									sx={{
										color: "#777777",
										textTransform: "capitalize",
										fontSize: "clamp(12px, 1.5vw, 16px)",
									}}
									onClick={() =>
										redirectTo(
											`/parliament/organes/${resource.mandatPrincipal?.organeRefUid}`,
										)
									}
								>
									{resource.mandatPrincipal?.codeQualite}{" "}
									{resource.mandatPrincipal?.organeRef.libelleEdition}{" "}
									{resource.mandatPrincipal?.organeRef.libelleAbrege}
								</Typography>
							)}
							<Typography
								variant="body2"
								className={styles.nombreMandats}
								sx={{ fontSize: "clamp(12px, 1.5vw, 16px)" }}
							>
								{displayActifMandat()}
							</Typography>
						</Box>
					</Stack>

					<Box
						display={"flex"}
						flexDirection={"row"}
						marginLeft={"auto"}
						gap="0.5em"
					>
						{displayWebAddresses()}
						<AlertButton targetClass={"Acteur"} targetId={resource.uid} />
					</Box>
				</Box>

				<Box
					display="flex"
					flexDirection="row"
					alignItems="start"
					gap="0.5em"
					sx={{ backgroundColor: "#fff", padding: "15px" }}
				>
					<Box
						display="grid"
						gridTemplateColumns="1fr"
						gap="1em"
						sx={{
							"@media (min-width: 768px)": {
								gridTemplateColumns: "1fr 1fr",
							},
						}}
					>
						{!isGouvernement() && (
							<Box display="flex" flexDirection="column">
								<UiBox className={styles.uiBox} title="Groupe politique">
									{resource.groupeParlementaire ? (
										// biome-ignore lint/a11y/useKeyWithClickEvents: no need to implement key events
										<div
											onClick={() =>
												redirectTo(
													`/parliament/organes/${resource.groupeParlementaireUid}`,
												)
											}
										>
											<GroupLabel
												groupName={resource.groupeParlementaire?.libelle}
												chamber={resource.groupeParlementaire?.chambre}
												color={resource.groupeParlementaire?.couleurAssociee}
												className={styles.linkHover}
												onClick={() =>
													redirectTo(
														`/parliament/organes/${resource.groupeParlementaireUid}`,
													)
												}
												size="small"
											/>
										</div>
									) : (
										<i>Pas de groupe politique actuellement</i>
									)}
								</UiBox>
							</Box>
						)}
						<Box display="flex" flexDirection="column">
							<UiBox className={styles.uiBox} title="Âge">
								<div>{formatDateNaisToAge("dateNais", resource)} ans</div>
							</UiBox>
						</Box>
						{!isGouvernement() && (
							<Box display="flex" flexDirection="column">
								<UiBox className={styles.uiBox} title="Commission">
									{resource.commissionPermanente ? (
										// biome-ignore lint/a11y/useKeyWithClickEvents: no need to implement key events
										<div
											className={styles.link}
											onClick={() =>
												redirectTo(
													`/parliament/organes/${resource.commissionPermanenteRefUid}`,
												)
											}
										>
											{resource.commissionPermanente?.libelle}
										</div>
									) : (
										<i>Pas de commission actuellement</i>
									)}
								</UiBox>
							</Box>
						)}
						<Box display="flex" flexDirection="column">
							<UiBox className={styles.uiBox} title="Profession">
								{resource.profession ? (
									<div>{resource.profession}</div>
								) : (
									<i>Profession non disponible</i>
								)}
							</UiBox>
						</Box>
						{resource.collaborateurs?.length > 0 && (
							<Box display="flex" flexDirection="column">
								<UiBox className={styles.uiBox} title="Collaborateurs">
									<Box display="flex" flexDirection="column">
										{resource.collaborateurs?.map((collab) => {
											return (
												<MemberBox
													firstName={collab.prenom}
													lastName={collab.nom}
													key={collab.id}
													size="small"
													layout="oneLine"
												/>
											);
										})}
									</Box>
								</UiBox>
							</Box>
						)}
						{displayContacts() != null && (
							<Box display="flex" flexDirection="column">
								<UiBox className={styles.uiBox} title="Contacts">
									{displayContacts()}
								</UiBox>
							</Box>
						)}
					</Box>

					<Box flex={2} className={styles.svgContainer}>
						{resource.chambre === "AN" && (
							<AssembleeHemicycle
								className={styles.svgIcon}
								highlightedSeats={[
									{
										id: Number.parseInt(
											resource.mandatPrincipal?.placeHemicycle,
										),
										color: resource.groupeParlementaire?.couleurAssociee,
										label: (
											<div style={{ padding: "2px 6px" }}>
												Siège {resource.mandatPrincipal?.placeHemicycle}
											</div>
										),
										hoverable: false,
										focusable: false,
									},
								]}
							/>
						)}

						{resource.chambre === "SN" && (
							<SenatHemicycle
								className={styles.svgIcon}
								highlightedSeats={[
									{
										id: Number.parseInt(resource.placeHemicycle),
										color: resource.groupeParlementaire?.couleurAssociee,
										label: (
											<div style={{ padding: "2px 6px" }}>
												Siège {resource.placeHemicycle}
											</div>
										),
										hoverable: false,
										focusable: false,
									},
								]}
							/>
						)}
					</Box>
				</Box>

				{!isGouvernement() && (
					<Box sx={{ width: "100%", marginTop: "2em" }}>
						<div className={styles.statTitle}>
							STATISTIQUES : XVII<sup>e</sup> législature
						</div>
						<ActeurDashboard acteur={resource} />
					</Box>
				)}

				<Box sx={{ width: "100%", marginTop: "3em" }}>
					<MultiList
						datasets={{
							dossiers: {
								...dossierDataset,
								columns: dossierDataset.columns.filter(
									(column) =>
										!["chambre", "acteurPrincipalRef"].includes(
											column.accessorKey,
										),
								),
							},
							votes: {
								...voteDataset,
								searchTable: "scrutinRef",
								columns: voteDataset.columns.filter(
									(column) =>
										!["scrutinRef.chambre", "acteurRef"].includes(
											column.accessorKey,
										),
								),
								rowRedirect: (item) =>
									`/parliament/scrutins/${item.scrutinRefUid}`,
							},
							amendements: {
								...amendementDataset,
								columns: amendementDataset.columns.filter(
									(column) =>
										!["chambre", "acteurRef"].includes(column.accessorKey),
								),
								rowRedirect: (item) =>
									`/parliament/acteurs/${resource.uid}/amendements/${
										item.uid
									}?${searchParams.toString()}`,
								disablePushToHistoryOnRowClick: true,
							},
							documents: {
								...documentDataset,
								columns: documentDataset.columns.filter(
									(column) =>
										!["chambre", "auteurPrincipal"].includes(
											column.accessorKey,
										),
								),
							},
							questions: {
								...questionDataset,
								columns: questionDataset.columns.filter(
									(column) =>
										!["chambre", "acteurRef"].includes(column.accessorKey),
								),
							},
							interventions: {
								...interventionDataset,
								columns: interventionDataset.columns.filter(
									(column) =>
										!["chambre", "acteurRef"].includes(column.accessorKey),
								),
							},
						}}
						showSearch={true}
						searchPlaceholder={"Rechercher dans l'activité du parlementaire..."}
						globalHiddenFilters={{ acteurRefUid: resource.uid }}
						disableSyncWithLocation={false}
						locationPrefix={`/parliament/acteurs/${resource.uid}`}
						disablePushToHistory={true}
					/>
				</Box>
			</div>

			<Drawer
				open={!!amendementId}
				onClose={() => {
					navigate(
						`/parliament/acteurs/${
							resource.uid
						}/${dataset}?${searchParams.toString()}`,
						{
							replace: true,
						},
					);
				}}
				anchor="right"
				sx={{ zIndex: 100 }}
			>
				<Show
					resourceId={amendementId}
					resource="amendements"
					includes={[
						"acteurRef.groupeParlementaire",
						"acteurRef.mandatPrincipal",
						"documentRef.dossierRef",
						"gouvernementOrganeRef",
					]}
					backIcon={<CloseIcon />}
					backText={"Fermer"}
					backLink={`/parliament/acteurs/${
						resource.uid
					}/${dataset}?${searchParams.toString()}`}
					disablePushToHistory
				>
					<AmendementShow />
				</Show>
			</Drawer>
		</>
	);
};

export const ActeurShow = () => {
	return (
		<Show
			resource={"acteurs"}
			includes={[
				"groupeParlementaire",
				"mandatPrincipal.organeRef",
				"collaborateurs",
				"circonscription",
				"commissionPermanente",
				"adressesElectroniques",
				"adressesPostales",
			]}
		>
			<ActeurDetails />
		</Show>
	);
};
